import * as React from "react";
import { availableCustomizationAreaMethods } from "../../types/ProductionMethodOption";
import { query } from "./graph";
import { updateOrderProductionJobCustomizationAreaMutation } from "../../graph";
import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CustomizationAreaForm from "../CustomizationAreaForm";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import PendingStatusView from "../PendingStatusView";
import Typography from "@mui/material/Typography";

const defaultCustomizationAreaFormInputErrors = {
  autobase: [],
  colorCount: [],
  colors: [],
  colorsInput: [],
  customizationAreaTemplateId: [],
  distressed: [],
  flatten: [],
  halftones: [],
  imageRepair: [],
  method: [],
  note: [],
  orderProductionJobId: [],
  recreate: [],
  splitIntoColors: [],
  sponsorback: [],
  vinylConfiguration: []
};

const defaultVinylConfigurationInputErrors = {
  names: [],
  namesColorId: [],
  namesFontId: [],
  numbers: [],
  numbersColorId: [],
  numbersFontId: [],
  size: []
};

const UpdateCustomizationAreaModal = ({
  customizationArea,
  isOpen,
  onRequestClose,
  orderProductionJobId
}) => {
  // Query:
  const { data, error, loading } = useQuery(query, {
    variables: { orderProductionJobId },
    skip: !isOpen
  });

  // Mutations:
  const [updateOrderProductionJobCustomizationArea] = useMutation(
    updateOrderProductionJobCustomizationAreaMutation
  );

  // States:
  const [isUpdatingCustomizationArea, setIsUpdatingCustomizationArea] =
    useState(false);
  const [
    customizationAreaFormInputErrors,
    setCustomizationAreaFormInputErrors
  ] = useState(defaultCustomizationAreaFormInputErrors);
  const [generalErrors, setGeneralErrors] = useState([]);
  const [vinylConfigurationInputErrors, setVinylConfigurationInputErrors] =
    useState(defaultVinylConfigurationInputErrors);

  const customizationAreaToUpdate = { ...customizationArea };

  // Handlers:
  const handleCancelButtonClick = () => onRequestClose();

  const handleCustomizationAreaFormUpdated = update =>
    Object.assign(customizationAreaToUpdate, update);

  const handleModalRequestClose = () => onRequestClose();

  const handleCustomizationAreaFormSubmit = () => {
    let hasErrors = false;
    let vinylConfigurationErrors = {
      ...defaultVinylConfigurationInputErrors
    };
    let customizationAreaErrors = {
      ...defaultCustomizationAreaFormInputErrors
    };

    const isVinylConfigured =
      customizationAreaToUpdate.vinylConfiguration &&
      (customizationAreaToUpdate.vinylConfiguration.names ||
        customizationAreaToUpdate.vinylConfiguration.numbers);

    const vinylConfiguration = customizationAreaToUpdate.vinylConfiguration;
    if (vinylConfiguration) {
      if (vinylConfiguration.names) {
        if (vinylConfiguration.namesColorId === null) {
          vinylConfigurationErrors.namesColorId = ["is required"];
          hasErrors = true;
        }

        if (vinylConfiguration.namesFontId === null) {
          vinylConfigurationErrors.namesFontId = ["is required"];
          hasErrors = true;
        }
      }

      if (vinylConfiguration.numbers) {
        if (vinylConfiguration.numbersColorId === null) {
          vinylConfigurationErrors.numbersColorId = ["is required"];
          hasErrors = true;
        }

        if (vinylConfiguration.numbersFontId === null) {
          vinylConfigurationErrors.numbersFontId = ["is required"];
          hasErrors = true;
        }
      }
    }

    if (customizationAreaToUpdate.template.id === null) {
      customizationAreaErrors.customizationAreaTemplateId = ["is required"];
      hasErrors = true;
    }

    setVinylConfigurationInputErrors(vinylConfigurationErrors);
    setCustomizationAreaFormInputErrors(customizationAreaErrors);

    if (hasErrors) {
      return;
    }

    const variables = {
      autobase: customizationAreaToUpdate.autobase,
      colorCount: customizationAreaToUpdate.colorCount,
      customizationAreaTemplateId: customizationAreaToUpdate.template.id,
      distressed: customizationAreaToUpdate.distressed,
      flatten: customizationAreaToUpdate.flatten,
      halftones: customizationAreaToUpdate.halftones,
      imageRepair: customizationAreaToUpdate.imageRepair,
      method: customizationAreaToUpdate.method,
      orderProductionJobCustomizationAreaId: customizationAreaToUpdate.id,
      recreate: customizationAreaToUpdate.recreate,
      splitIntoColors: customizationAreaToUpdate.splitIntoColors,
      sponsorback: customizationAreaToUpdate.sponsorback,
      vinylConfiguration: isVinylConfigured
        ? customizationAreaToUpdate.vinylConfiguration
        : null
    };

    setIsUpdatingCustomizationArea(true);
    updateOrderProductionJobCustomizationArea({ variables })
      .then(
        ({
          data: {
            updateOrderProductionJobCustomizationArea: { errors, succeeded }
          }
        }) => {
          if (succeeded) {
            onRequestClose();
          } else {
            setCustomizationAreaFormInputErrors(errors);
          }
        }
      )
      .catch(e => {
        setGeneralErrors([e.message]);
      })
      .finally(() => setIsUpdatingCustomizationArea(false));
  };

  return (
    <Dialog
      aria-describedby="Add Customization Area Modal"
      aria-labelledby="AddCustomizationAreaModal"
      open={isOpen}
      onClose={handleModalRequestClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogTitle>Update Customization Area</DialogTitle>
      {!data ? (
        <PendingStatusView status="Waiting" />
      ) : loading ? (
        <PendingStatusView status="Loading" />
      ) : error ? (
        <div>
          <Typography variant="body2">{error.message}</Typography>
        </div>
      ) : (
        <DialogContent>
          {generalErrors && generalErrors.length > 0 ? (
            <Typography color="error">{generalErrors.join(", ")}</Typography>
          ) : isUpdatingCustomizationArea ? (
            <PendingStatusView status="Loading" />
          ) : (
            <CustomizationAreaForm
              availableCustomizationAreaTemplates={
                data.orderProductionJob.availableCustomizationAreaTemplates
              }
              availableCustomizationAreaMethods={
                availableCustomizationAreaMethods
              }
              customizationArea={customizationArea}
              inputErrors={customizationAreaFormInputErrors}
              onCustomizationAreaUpdated={handleCustomizationAreaFormUpdated}
              vinylColors={data.vinylColors}
              vinylConfigurationInputErrors={vinylConfigurationInputErrors}
              vinylFonts={data.vinylFonts}
            />
          )}
          <DialogActions>
            <Button
              color="secondary"
              disabled={isUpdatingCustomizationArea}
              onClick={handleCancelButtonClick}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isUpdatingCustomizationArea}
              onClick={handleCustomizationAreaFormSubmit}
            >
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UpdateCustomizationAreaModal;
